// frontend/src/pages/PayrollDone/components/NextSteps.tsx
// "What's next" section: 2 numbered action steps for post-run work.
// Step 1 (shown only when there are cheques): Print pay stubs.
// Step 2: Remit CRA taxes.

import React from "react";
import type { DoneViewEmployee } from "../types";
import { fmtMoney, fmtDateFull } from "../helpers";
import { C, FONT, outlineBtnSmall, textBtn } from "../styles";

interface NextStepsProps {
  chequeCount: number;
  empsPaid: number;
  craTotal: number;
  employeeTax: number;
  employerTax: number;
  payDate?: string | null;
  employees: DoneViewEmployee[];
  onViewLiabilities: () => void;
}

export default function NextSteps({
  chequeCount,
  empsPaid,
  craTotal,
  employeeTax,
  employerTax,
  payDate,
  employees,
  onViewLiabilities,
}: NextStepsProps): React.ReactElement {
  const empWord = empsPaid === 1 ? "employee" : "employees";
  const chequeWord = chequeCount === 1 ? "cheque" : "cheques";
  const hasCheques = chequeCount > 0;

  function printAllPayStubs(): void {
    // One tab per pay stub, staggered 200ms so browsers don't block them.
    employees.forEach(function (emp, idx) {
      setTimeout(function () {
        window.open("/payroll/paycheques/" + emp.stub_id, "_blank");
      }, idx * 200);
    });
  }

  return (
    <div style={{ marginBottom: 40 }}>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: C.ink,
          marginBottom: 20,
          paddingBottom: 12,
          borderBottom: "1.5px solid " + C.ink,
        }}
      >
        What's next
      </div>

      {/* Step 1: Write cheques (only if there are cheques) */}
      {hasCheques && (
        <StepRow number={1}>
          <StepTitle>Write {chequeCount} {chequeWord}</StepTitle>
          <StepBody>
            Deliver to your {empWord} by{" "}
            <strong>{fmtDateFull(payDate)}</strong>. Enter the cheque number in the table below.
          </StepBody>
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button onClick={printAllPayStubs} style={outlineBtnSmall}>
              Print pay stubs
            </button>
          </div>
        </StepRow>
      )}

      {/* Step 2: Remit to CRA */}
      <StepRow number={hasCheques ? 2 : 1} isLast>
        <StepTitle>Remit {fmtMoney(craTotal)} in taxes to CRA</StepTitle>
        <StepBody>
          {fmtMoney(employeeTax)} withheld from {empWord} plus {fmtMoney(employerTax)} in
          employer contributions. Novala tracks this on your payroll liabilities.
        </StepBody>
        <div style={{ marginTop: 12 }}>
          <button onClick={onViewLiabilities} style={textBtn}>
            View liabilities
          </button>
        </div>
      </StepRow>
    </div>
  );
}

// ── Small helpers kept local — only used by NextSteps ─────────────────────
function StepRow({
  number,
  isLast = false,
  children,
}: {
  number: number;
  isLast?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      style={{
        padding: "20px 0",
        borderBottom: isLast ? "none" : "1px solid " + C.line,
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: C.ink,
          color: C.card,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 700,
          flexShrink: 0,
          fontFamily: FONT,
        }}
      >
        {number}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function StepTitle({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 4 }}>
      {children}
    </div>
  );
}

function StepBody({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ fontSize: 13, color: C.ink, fontWeight: 500, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}